import { z } from "zod";
export const cityProgressSchema = z.object({
    id: z.string().min(1),
    name: z.string().min(1),
    countryOrRegion: z.string().min(1),
    latitude: z.number().min(-90).max(90),
    longitude: z.number().min(-180).max(180),
    level: z.number().int().min(1).max(5),
    contributors: z.number().int().nonnegative(),
    activeToday: z.number().int().nonnegative(),
    progress: z.number().min(0).max(1)
});
export const livePulseSchema = z.object({
    id: z.string().min(1),
    cityId: z.string().min(1),
    city: z.string().min(1),
    countryOrRegion: z.string().min(1),
    contributorNumber: z.number().int().positive(),
    occurredAt: z.iso.datetime()
});
export const worldSnapshotSchema = z.object({
    campaignId: z.string().min(1),
    campaignName: z.string().min(1),
    startsAt: z.iso.datetime(),
    endsAt: z.iso.datetime(),
    walkers: z.number().int().nonnegative(),
    relayPasses: z.number().int().nonnegative().default(0),
    citiesLit: z.number().int().nonnegative(),
    regionsReached: z.number().int().nonnegative(),
    areaSquareKilometers: z.number().nonnegative(),
    totalSteps: z.number().int().nonnegative(),
    stepGoal: z.number().int().positive(),
    missionProgress: z.number().min(0).max(1),
    recentPulses: z.array(livePulseSchema).max(30)
});
export const routePointSchema = z.object({
    latitude: z.number().min(-90).max(90),
    longitude: z.number().min(-180).max(180),
    horizontalAccuracy: z.number().positive().max(10_000),
    speedMetersPerSecond: z.number().min(-1).max(200),
    recordedAt: z.iso.datetime(),
    simulatedBySoftware: z.boolean().default(false)
});
export const completeWalkRequestSchema = z.object({
    campaignId: z.string().min(1),
    cityId: z.string().min(1),
    inviteCode: z.string().regex(/^[A-Za-z0-9_-]{10,32}$/).optional(),
    attestChallenge: z.string().regex(/^[A-Za-z0-9_-]{43}$/).optional(),
    startedAt: z.iso.datetime(),
    endedAt: z.iso.datetime(),
    steps: z.number().int().nonnegative().max(250_000),
    points: z.array(routePointSchema).min(2).max(2_000)
});
export const walkRejectionCodeSchema = z.enum([
    "duration_too_short",
    "route_span_too_short",
    "session_too_long",
    "distance_too_short",
    "not_enough_trusted_points",
    "simulated_route",
    "invalid_time_range",
    "excessive_risk"
]);
export const walkValidationResultSchema = z.object({
    qualified: z.boolean(),
    durationSeconds: z.number().nonnegative(),
    trustedDistanceMeters: z.number().nonnegative(),
    trustedPointCount: z.number().int().nonnegative(),
    rejectedPointCount: z.number().int().nonnegative(),
    publicCells: z.array(z.string()),
    riskScore: z.number().min(0).max(100),
    rejectionCodes: z.array(walkRejectionCodeSchema),
    rawEvidenceExpiresAt: z.iso.datetime()
});
export const completeWalkResponseSchema = z.object({
    sessionId: z.uuid(),
    contributorNumber: z.number().int().positive(),
    inviteCode: z.string().regex(/^[A-Za-z0-9_-]{10,32}$/).optional(),
    city: cityProgressSchema,
    validation: walkValidationResultSchema,
    snapshot: worldSnapshotSchema,
    pulse: livePulseSchema.optional()
});
export const inviteDetailSchema = z.object({
    code: z.string().regex(/^[A-Za-z0-9_-]{10,32}$/),
    campaignId: z.string().min(1),
    campaignName: z.string().min(1),
    cityId: z.string().min(1),
    city: z.string().min(1),
    countryOrRegion: z.string().min(1),
    contributorNumber: z.number().int().positive(),
    clicks: z.number().int().nonnegative(),
    conversions: z.number().int().nonnegative(),
    createdAt: z.iso.datetime(),
    expiresAt: z.iso.datetime()
});
export const inviteClickResponseSchema = z.object({
    accepted: z.boolean()
});
export const appStoreClickRequestSchema = z.object({
    campaignId: z.string().min(1).max(80),
    inviteCode: z.string().regex(/^[A-Za-z0-9_-]{10,32}$/).optional()
}).strict();
export const appStoreClickResponseSchema = z.object({
    accepted: z.boolean(),
    inviteAttributed: z.boolean()
}).strict();
export const appAttestPurposeSchema = z.enum(["attestation", "walk"]);
export const appAttestChallengeRequestSchema = z.object({
    purpose: appAttestPurposeSchema
}).strict();
export const appAttestChallengeResponseSchema = z.object({
    challenge: z.string().regex(/^[A-Za-z0-9_-]{43}$/),
    expiresAt: z.iso.datetime()
});
export const appAttestRegisterRequestSchema = z.object({
    challenge: z.string().regex(/^[A-Za-z0-9_-]{43}$/),
    keyId: z.string().min(40).max(128).regex(/^[A-Za-z0-9+/]+={0,2}$/),
    attestation: z.string().min(100).max(250_000)
}).strict();
export const appAttestRegisterResponseSchema = z.object({
    accepted: z.literal(true),
    environment: z.enum(["development", "production"])
});
export const deleteMyDataResponseSchema = z.object({
    deletedWalks: z.number().int().nonnegative(),
    deletedRawEvidence: z.number().int().nonnegative(),
    deletedAnalyticsEvents: z.number().int().nonnegative(),
    deletedAttestations: z.number().int().nonnegative()
});
export const personalDataInviteSchema = z.object({
    code: z.string().regex(/^[A-Za-z0-9_-]{10,32}$/),
    clicks: z.number().int().nonnegative(),
    conversions: z.number().int().nonnegative(),
    createdAt: z.iso.datetime(),
    expiresAt: z.iso.datetime()
});
export const personalDataWalkSchema = z.object({
    sessionId: z.uuid(),
    campaignId: z.string().min(1),
    cityId: z.string().min(1),
    city: z.string().min(1),
    countryOrRegion: z.string().min(1),
    startedAt: z.iso.datetime(),
    endedAt: z.iso.datetime(),
    createdAt: z.iso.datetime(),
    steps: z.number().int().nonnegative(),
    qualified: z.boolean(),
    durationSeconds: z.number().nonnegative(),
    trustedDistanceMeters: z.number().nonnegative(),
    trustedPointCount: z.number().int().nonnegative(),
    rejectedPointCount: z.number().int().nonnegative(),
    publicCells: z.array(z.string()),
    riskScore: z.number().min(0).max(100),
    rejectionCodes: z.array(walkRejectionCodeSchema),
    rawRouteEvidence: z.object({
        expiresAt: z.iso.datetime(),
        points: z.array(routePointSchema).max(2_000)
    }).nullable(),
    sourceInviteCode: z.string().regex(/^[A-Za-z0-9_-]{10,32}$/).optional(),
    issuedInvite: personalDataInviteSchema.optional()
});
export const deviceIntegrityKeyExportSchema = z.object({
    keyId: z.string().min(40).max(128),
    environment: z.enum(["development", "production"]),
    validationCategory: z.number().int().min(0).max(10),
    bundleVersion: z.string().min(1).max(32),
    signCount: z.number().int().nonnegative()
});
const analyticsBaseShape = {
    id: z.uuid(),
    occurredAt: z.iso.datetime(),
    campaignId: z.string().min(1).max(80).optional(),
    sessionId: z.uuid().optional()
};
const authorizationStateSchema = z.enum([
    "not_determined",
    "authorized",
    "reduced_accuracy",
    "denied",
    "restricted",
    "unavailable"
]);
export const clientAnalyticsEventSchema = z.discriminatedUnion("name", [
    z.object({
        ...analyticsBaseShape,
        name: z.literal("app_open"),
        properties: z.object({
            locale: z.string().min(2).max(24),
            appVersion: z.string().min(1).max(24)
        }).strict()
    }).strict(),
    z.object({
        ...analyticsBaseShape,
        name: z.literal("world_loaded"),
        properties: z.object({
            source: z.enum(["launch", "walk", "manual"]),
            live: z.boolean()
        }).strict()
    }).strict(),
    z.object({
        ...analyticsBaseShape,
        name: z.literal("permission_prompt_viewed"),
        properties: z.object({
            authorization: authorizationStateSchema
        }).strict()
    }).strict(),
    z.object({
        ...analyticsBaseShape,
        name: z.literal("walk_started"),
        properties: z.object({
            authorization: authorizationStateSchema,
            restored: z.boolean()
        }).strict()
    }).strict(),
    z.object({
        ...analyticsBaseShape,
        name: z.literal("walk_completed"),
        properties: z.object({
            qualified: z.boolean(),
            durationBucket: z.enum(["under_5m", "5_10m", "10_30m", "over_30m"]),
            distanceBucket: z.enum(["under_250m", "250_1000m", "1_5km", "over_5km"]),
            rejectionCount: z.number().int().min(0).max(8)
        }).strict()
    }).strict(),
    z.object({
        ...analyticsBaseShape,
        name: z.literal("share_video_ready"),
        properties: z.object({
            renderBucket: z.enum(["under_2s", "2_5s", "over_5s"]),
            sizeBucket: z.enum(["under_2mb", "2_5mb", "over_5mb"])
        }).strict()
    }).strict(),
    z.object({
        ...analyticsBaseShape,
        name: z.literal("share_sheet_opened"),
        properties: z.object({
            cityId: z.string().min(1).max(120)
        }).strict()
    }).strict(),
    z.object({
        ...analyticsBaseShape,
        name: z.literal("share_completed"),
        properties: z.object({
            completed: z.boolean()
        }).strict()
    }).strict()
]);
export const appAttestResultAnalyticsEventSchema = z.object({
    ...analyticsBaseShape,
    name: z.literal("app_attest_result"),
    properties: z.object({
        stage: z.enum(["registration", "walk"]),
        success: z.boolean(),
        mode: z.enum(["off", "observe", "enforce"]),
        code: z.string().min(1).max(80).regex(/^[a-z0-9_]+$/)
    }).strict()
}).strict();
export const analyticsEventSchema = z.union([
    clientAnalyticsEventSchema,
    appAttestResultAnalyticsEventSchema
]);
export const exportMyDataResponseSchema = z.object({
    schemaVersion: z.literal(1),
    exportedAt: z.iso.datetime(),
    installationId: z.string().min(16).max(128),
    walks: z.array(personalDataWalkSchema),
    analyticsEvents: z.array(analyticsEventSchema),
    deviceIntegrityKeys: z.array(deviceIntegrityKeyExportSchema),
    cryptographicMaterialIncluded: z.literal(false)
});
export const analyticsBatchRequestSchema = z.object({
    events: z.array(clientAnalyticsEventSchema).min(1).max(25)
}).strict();
export const analyticsBatchResponseSchema = z.object({
    accepted: z.number().int().nonnegative()
});
export const apiErrorSchema = z.object({
    error: z.string(),
    message: z.string(),
    requestId: z.string().optional()
});
//# sourceMappingURL=index.js.map